import {
  Box,
  BoxProps,
  Button,
  Flex,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Portal,
  VStack,
} from '@chakra-ui/react'
import { ContentNodeUtils, InputValueContentNode } from '@helo/cms'
import { Assert, List, pipe } from '@helo/utils'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  getBoundaryProps,
  useClickOutsideBoundary,
} from '../../../hooks/useClickOutsideBoundary'
import { useIsDark } from '../../../hooks/useIsDark'
import { CubeDistinctValues } from '../../../services/cubeDistinctValuesQuery'
import { createDistinctValuesLoadOptions } from '../../../services/distinctValuesQuery'
import { ErrorBoundary } from '../../ErrorBoundary/ErrorBoundary'
import { WidgetError } from '../../Errors/WidgetError'
import { FilterButton } from '../FilterButton/FilterButton'
import { FilterChipsEditor } from '../FilterChipsEditor/FilterChipsEditor'
import { FilterEditorRow } from '../FilterEditorRow'
import {
  FILTER_EDITOR_BOUNDARY_ID,
  type LoadOptionsForPickListFn,
  type LoadOptionsForPickListParams,
  buildAppliedMergedFilterData,
  buildDistinctRootFilter,
  buildMergedFilterDataFromRows,
  getFilterObjectContentNode,
  getUnmappedTypeName,
  hasFilterEditorApplyChanges,
  hasFilterValue,
  isDistinctValuesFilterInputType,
  useAvailableFilterFieldsDataTypeConfigs,
  validateFilterEditorRows,
} from './FilterEditor.parts'

export type FilterEditorProps = {
  /** Array of filter configurations to display */
  inputValue: InputValueContentNode
  onChange: (inputValueContentNode: InputValueContentNode) => void
  /** Whether to show the add filter button */
  showAddButton?: boolean
  /** Async load options for "Is Any Of" pick list (distinct query uses correct FilterInput type for nested fields). */
  loadOptionsForPickList?: LoadOptionsForPickListFn
  /** When true (default), date conditions (before/after/excludes) are in the condition dropdown; when false, legacy behavior (pills in calendar). */
  dateConditionsInDropdown?: boolean
  /** When set, overrides Chakra color mode for dark/light styling (e.g. from widget config). */
  colorMode?: 'light' | 'dark'
  /** When true, the add-filter popover is open on mount (e.g. for Storybook). */
  defaultIsPopoverOpen?: boolean
}

export const FilterEditor = ({
  inputValue: initialInputValue,
  onChange,
  showAddButton = true,
  loadOptionsForPickList: loadOptionsForPickListProp,
  dateConditionsInDropdown = true,
  colorMode: colorModeOverride,
  defaultIsPopoverOpen = false,
  ...props
}: FilterEditorProps) => {
  const isDark = useIsDark(colorModeOverride)

  const [isAddPopoverOpen, setIsAddPopoverOpen] =
    useState(!!defaultIsPopoverOpen)
  const [filteredFields, setFilteredFields] = useState<InputValueContentNode[]>(
    [],
  )
  const filteredFieldsRef = useRef(filteredFields)
  const popoverContentRef = useRef<HTMLDivElement>(null)
  const [autoFocusIndex, setAutoFocusIndex] = useState<number>(0)
  const [rowErrors, setRowErrors] = useState<Set<number>>(new Set())
  const [focusConditionForRow, setFocusConditionForRow] = useState<
    number | null
  >(null)

  useEffect(() => {
    filteredFieldsRef.current = filteredFields
  }, [filteredFields])

  // After a field is selected on a new row, the row remounts (key changes).
  // Focus the value input (skip operator since a default is auto-selected).
  useEffect(() => {
    if (focusConditionForRow === null) return
    const rowIndex = focusConditionForRow
    setFocusConditionForRow(null)
    const rows = popoverContentRef.current?.querySelectorAll(
      '[data-testid="filter-editor-row"]',
    )
    if (rows?.[rowIndex]) {
      const inputs = rows[rowIndex].querySelectorAll('input')
      if (inputs?.[2]) (inputs[2] as HTMLElement).focus()
      else if (inputs?.[1]) (inputs[1] as HTMLElement).focus()
    }
  }, [focusConditionForRow])

  // Close on click outside. useClickOutsideBoundary treats ref + data-click-boundary as inside (pick list and its portaled menu set this so they don't close the popover)
  useClickOutsideBoundary({
    ref: popoverContentRef,
    boundaryId: FILTER_EDITOR_BOUNDARY_ID,
    onClickOutside: () => setIsAddPopoverOpen(false),
    enabled: isAddPopoverOpen,
    excludeFloatingUI: true,
  })

  const filterObjectContentNode = useMemo(() => {
    return getFilterObjectContentNode(initialInputValue)
  }, [initialInputValue])

  const filterObjectDataTypeConfig = useMemo(() => {
    return filterObjectContentNode.getDataTypeConfig()
  }, [filterObjectContentNode])

  const filterObjectTypeName = useMemo(
    () =>
      (filterObjectDataTypeConfig as { typeName?: string } | undefined)
        ?.typeName,
    [filterObjectDataTypeConfig],
  )

  const unmappedFilterTypeName = useMemo(() => {
    // Config has meta.unmappedPath (DataTypeLocator); helper accepts that shape
    return getUnmappedTypeName(
      filterObjectDataTypeConfig as Parameters<typeof getUnmappedTypeName>[0],
    )
  }, [filterObjectDataTypeConfig])

  const loadOptionsForPickList = useMemo(() => {
    if (loadOptionsForPickListProp) return loadOptionsForPickListProp
    const distinctLoadOptions = createDistinctValuesLoadOptions()
    const cubeDistinctLoadOptions = CubeDistinctValues.createLoadOptions()
    return (params: LoadOptionsForPickListParams) => {
      const fieldName = params.unmappedFieldKey ?? ''

      if (params.cubeDistinctView && fieldName) {
        return cubeDistinctLoadOptions({
          ...params,
          unmappedFieldKey: fieldName,
        })
      }

      const typeName =
        params.unmappedTypeName ??
        unmappedFilterTypeName ??
        filterObjectTypeName ??
        ''
      if (!typeName || !fieldName) return Promise.resolve([])
      // Avoid GraphQL errors for cube *WhereInput roots that are not on
      // distinctValues (field-level filterDistinctInputType overrides this).
      if (!isDistinctValuesFilterInputType(typeName)) {
        return Promise.resolve([])
      }

      const sourceFields = isAddPopoverOpen
        ? filteredFields
        : (pipe(
            filterObjectContentNode,
            ContentNodeUtils.getFields(),
          ) as InputValueContentNode[])

      return distinctLoadOptions({
        ...params,
        unmappedTypeName: typeName,
        unmappedFieldKey: fieldName,
        rootFilter:
          params.rootFilter ??
          buildDistinctRootFilter(sourceFields, [params.currentFieldKey ?? '']),
      })
    }
  }, [
    loadOptionsForPickListProp,
    unmappedFilterTypeName,
    filterObjectTypeName,
    isAddPopoverOpen,
    filteredFields,
    filterObjectContentNode,
  ])

  const availableFilters = useAvailableFilterFieldsDataTypeConfigs(
    filterObjectDataTypeConfig,
  )

  const hasFiltersApplied = useMemo(() => {
    return pipe(
      initialInputValue,
      ContentNodeUtils.getOfType(),
      Assert.orThrow(
        ContentNodeUtils.isKind('INPUT_OBJECT'),
        () => new Error('Expected INPUT_OBJECT'),
      ),
      ContentNodeUtils.getFields(),
      List.some((field: InputValueContentNode) => hasFilterValue(field.data)),
    )
  }, [initialInputValue])

  // Build initial filteredFields (content nodes per row) when popover opens
  const initialFilteredFields = useMemo(() => {
    if (!isAddPopoverOpen) {
      return []
    }

    const fields = pipe(filterObjectContentNode, ContentNodeUtils.getFields())
    // Order applied rows by the filter object's data key order (insertion order)
    // rather than schema order, so filters added from a column header — which are
    // appended to the end of the filter object — appear at the bottom of the list.
    const fieldsByKey = new Map(fields.map(field => [field.key, field]))
    const objectData =
      filterObjectContentNode.data &&
      typeof filterObjectContentNode.data === 'object' &&
      !Array.isArray(filterObjectContentNode.data)
        ? (filterObjectContentNode.data as Record<string, unknown>)
        : {}
    const existing: InputValueContentNode[] = []
    const seenKeys = new Set<string>()
    for (const key of Object.keys(objectData)) {
      const field = fieldsByKey.get(key)
      if (field && hasFilterValue(field.data)) {
        existing.push(field)
        seenKeys.add(key)
      }
    }
    // Defensive: include any applied field not represented in the data key order.
    for (const field of fields) {
      if (!seenKeys.has(field.key) && hasFilterValue(field.data)) {
        existing.push(field)
      }
    }

    if (availableFilters.length === 0) {
      return existing
    }

    const firstDefault = availableFilters[0]
    if (firstDefault === undefined) {
      throw new Error(
        'Filter editor: first default filter (availableFilters[0]) is undefined; cannot create initial empty row. Check that available filter fields are defined.',
      )
    }
    // Trailing empty row: key '' so "Select field" is shown (same template as Clear / Add row)
    const emptyRow = ContentNodeUtils.createContentNodeWithSelection(
      { ...firstDefault, key: '' },
      {},
    ) as InputValueContentNode

    if (existing.length === 0) {
      return [emptyRow]
    }

    return [...existing, emptyRow]
  }, [isAddPopoverOpen, filterObjectContentNode, availableFilters])

  const hasInitializedFieldsForOpenRef = useRef(false)
  useEffect(() => {
    if (!isAddPopoverOpen) {
      setFilteredFields([])
      hasInitializedFieldsForOpenRef.current = false
      setAutoFocusIndex(0)
      setRowErrors(new Set())
      return
    }
    if (!hasInitializedFieldsForOpenRef.current) {
      hasInitializedFieldsForOpenRef.current = true
      setFilteredFields(initialFilteredFields)
      setAutoFocusIndex(0)
    }
  }, [isAddPopoverOpen, initialFilteredFields])

  const handleRowUpdate = (
    index: number,
    contentNode: InputValueContentNode,
  ) => {
    const prevKey = filteredFields[index]?.key
    const newKey = contentNode.key
    // When a field is selected on a new/empty row, focus the condition select after remount
    if ((!prevKey || prevKey === '') && newKey && newKey !== '') {
      setFocusConditionForRow(index)
    }
    setFilteredFields(prev => {
      const next = [...prev]
      next[index] = contentNode
      filteredFieldsRef.current = next
      return next
    })
    // Clear error for this row when it's updated
    setRowErrors(prev => {
      if (!prev.has(index)) return prev
      const next = new Set(prev)
      next.delete(index)
      return next
    })
  }

  const handleRowRemove = (index: number) => {
    setFilteredFields(prev => {
      const next = prev.filter((_, i) => i !== index)
      filteredFieldsRef.current = next
      return next
    })
    // Reindex errors after removal
    setRowErrors(prev => {
      if (prev.size === 0) return prev
      const next = new Set<number>()
      for (const i of prev) {
        if (i < index) next.add(i)
        else if (i > index) next.add(i - 1)
      }
      return next
    })
  }

  const handleAddRow = () => {
    if (availableFilters.length === 0) return
    const emptyRow = ContentNodeUtils.createContentNodeWithSelection(
      { ...availableFilters[0], key: '' },
      {},
    ) as InputValueContentNode
    setAutoFocusIndex(filteredFields.length)
    setFilteredFields(prev => {
      const next = [...prev, emptyRow]
      filteredFieldsRef.current = next
      return next
    })
  }

  const buildMergedData = buildMergedFilterDataFromRows

  const appliedMergedData = useMemo(
    () => buildAppliedMergedFilterData(initialInputValue.data),
    [initialInputValue],
  )

  const hasApplyChanges = useMemo(
    () =>
      hasFilterEditorApplyChanges(
        buildMergedData(filteredFields),
        appliedMergedData,
      ),
    [filteredFields, appliedMergedData],
  )

  const validateRows = validateFilterEditorRows

  const handleApply = () => {
    const errors = validateRows(filteredFieldsRef.current, appliedMergedData)
    if (errors.size > 0) {
      setRowErrors(errors)
      return
    }

    const mergedData = buildMergedData(filteredFieldsRef.current)

    const updatedInputValue = pipe(
      ContentNodeUtils.updateContentNode(mergedData)(filterObjectContentNode),
      ContentNodeUtils.updateParentContentNode(initialInputValue),
      Assert.orThrow(
        ContentNodeUtils.isKind('INPUT_VALUE'),
        () => new Error('Expected INPUT_VALUE'),
      ),
    )

    onChange(updatedInputValue)

    setRowErrors(new Set())
    setIsAddPopoverOpen(false)
    setFilteredFields([])
  }

  const flexProps = {
    gap: '8px',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    ...props,
  }

  return (
    <ErrorBoundary renderError={() => <WidgetError />}>
      <Flex {...flexProps}>
        {showAddButton && availableFilters.length > 0 && (
          <Popover
            isOpen={isAddPopoverOpen}
            onOpen={() => setIsAddPopoverOpen(true)}
            onClose={() => setIsAddPopoverOpen(false)}
            placement="bottom-start"
            closeOnBlur={false}
            isLazy
            lazyBehavior="unmount"
          >
            <PopoverTrigger>
              <Box>
                <FilterButton
                  hasFiltersApplied={hasFiltersApplied}
                  isOpen={isAddPopoverOpen}
                  colorMode={colorModeOverride}
                />
              </Box>
            </PopoverTrigger>
            <Portal>
              <PopoverContent
                ref={popoverContentRef}
                data-part="FilterEditorPopover"
                data-testid="filter-editor-popover"
                {...getBoundaryProps(FILTER_EDITOR_BOUNDARY_ID)}
                borderRadius="4px"
                boxShadow={
                  isDark
                    ? '0 4px 24px 0 rgba(0, 0, 0, 0.24)'
                    : '0 3px 10px 0 rgba(0, 0, 0, 0.05), 0 4px 3px 0 rgba(0, 0, 0, 0.03)'
                }
                minWidth="826px"
                bg={isDark ? 'presets.dark.surfacePanel' : undefined}
                border={isDark ? '1px solid' : undefined}
                borderColor={isDark ? 'presets.neutral.90' : undefined}
              >
                <PopoverBody
                  padding="16px"
                  bg={isDark ? 'presets.dark.surfacePanel' : undefined}
                  color={isDark ? 'presets.neutral.0' : undefined}
                  onKeyDownCapture={e => {
                    // Use capture phase to check menu state BEFORE react-select closes it
                    if (e.key === 'Enter') {
                      const target = e.target as HTMLElement
                      // Let native Enter activation work for explicit interactive controls.
                      // Without this, Enter on focused button/link gets hijacked by modal apply.
                      const isNativeEnterTarget = !!target.closest(
                        'button, a[href], [role="button"], [role="link"]',
                      )
                      if (isNativeEnterTarget) return
                      // Check if target is in a select with open menu (using data attribute set on Control)
                      const controlWithOpenMenu = target.closest(
                        '[data-part="Control"][data-menu-open="true"]',
                      )
                      // Check if target is in a pick list with open menu
                      const isInPickListWithOpenMenu =
                        target.closest(
                          '[data-testid="filter-pick-list-filter"]',
                        ) &&
                        document.querySelector(
                          '[data-testid="filter-pick-list-menu"]',
                        )
                      // If a dropdown is open, let the dropdown handle Enter (don't apply modal)
                      if (controlWithOpenMenu || isInPickListWithOpenMenu) {
                        return
                      }
                      // No dropdown open - apply and close modal
                      e.preventDefault()
                      handleApply()
                    }
                  }}
                >
                  <VStack gap="8px" alignItems="start">
                    {filteredFields.map((contentNode, index) => (
                      <FilterEditorRow
                        key={`${index}-${contentNode.key}`}
                        inputValueContentNode={contentNode}
                        filters={availableFilters.filter(
                          filter =>
                            !filteredFields.some(
                              node =>
                                node.key === filter.key &&
                                node.key !== contentNode.key,
                            ),
                        )}
                        onUpdate={updated => handleRowUpdate(index, updated)}
                        onRemove={() => handleRowRemove(index)}
                        showRemoveButton={filteredFields.length > 1}
                        autoFocus={index === autoFocusIndex}
                        loadOptionsForPickList={loadOptionsForPickList}
                        isFilterEditorOpen={isAddPopoverOpen}
                        dateConditionsInDropdown={dateConditionsInDropdown}
                        validationError={rowErrors.has(index)}
                        colorMode={colorModeOverride}
                      />
                    ))}
                    <Button
                      data-testid="filter-editor-add-button"
                      variant="tertiary"
                      size="sm"
                      onClick={handleAddRow}
                      isDisabled={
                        filteredFields.length >= availableFilters.length
                      }
                    >
                      + Add {filteredFields.length > 0 ? 'Another' : 'a'} Filter
                    </Button>
                  </VStack>
                </PopoverBody>
                <PopoverFooter
                  padding="16px"
                  bg={isDark ? 'presets.dark.surfacePanel' : undefined}
                  color={isDark ? 'presets.neutral.0' : undefined}
                  borderTop={isDark ? '1px solid' : undefined}
                  borderColor={isDark ? 'presets.neutral.90' : undefined}
                >
                  <HStack gap="8px" justifyContent="space-between" width="100%">
                    <Button
                      data-testid="filter-editor-clear-button"
                      size="sm"
                      variant="tertiary"
                      onClick={() => {
                        // Remove all applied filters (save empty state) and close the modal.
                        // Same effect as the chips Clear button plus closing the popover.
                        setRowErrors(new Set())
                        setFilteredFields([])
                        filteredFieldsRef.current = []
                        setIsAddPopoverOpen(false)
                        pipe(
                          initialInputValue,
                          ContentNodeUtils.updateChildContentNode(
                            ContentNodeUtils.updateContentNode({})(
                              filterObjectContentNode,
                            ),
                          ),
                          onChange,
                        )
                      }}
                    >
                      Clear
                    </Button>
                    <HStack gap="8px" flexShrink={0}>
                      <Button
                        data-testid="filter-editor-cancel-button"
                        size="sm"
                        variant="tertiary"
                        onClick={() => {
                          setFilteredFields([])
                          setIsAddPopoverOpen(false)
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        data-testid="filter-editor-apply-button"
                        size="sm"
                        variant="primary"
                        onClick={handleApply}
                        isDisabled={!hasApplyChanges}
                      >
                        Apply
                      </Button>
                    </HStack>
                  </HStack>
                </PopoverFooter>
              </PopoverContent>
            </Portal>
          </Popover>
        )}
        <FilterChipsEditor
          filters={availableFilters}
          filterInputValue={initialInputValue}
          loadOptionsForPickList={loadOptionsForPickList}
          onChange={onChange}
          onClear={() => {
            setFilteredFields([])
            pipe(
              initialInputValue,
              ContentNodeUtils.updateChildContentNode(
                ContentNodeUtils.updateContentNode({})(filterObjectContentNode),
              ),
              onChange,
            )
          }}
          onClick={() => {
            if (isAddPopoverOpen) {
              setIsAddPopoverOpen(false)
              setFilteredFields([])
            }
          }}
        />
      </Flex>
    </ErrorBoundary>
  )
}

FilterEditor.displayName = 'FilterEditor'
